/**
 * Governance Posture & Readiness Page — M6.3 Phase 4
 * Recovery readiness, notification readiness, posture summary, export
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Shield, Plus, Loader2, AlertTriangle, ChevronLeft, CheckCircle2,
  Activity, ArrowRight, RefreshCw, TrendingUp, Download, Bell,
  Server, FileText, Target, AlertCircle, ShieldCheck, Eye
} from 'lucide-react';

const DOMAIN_CONFIG = {
  backup_restore: { icon: Server, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
  disaster_recovery: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/15' },
  business_continuity: { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  incident_response: { icon: Bell, color: 'text-amber-400', bg: 'bg-amber-500/15' },
  communication_plan: { icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/15' },
};

const STATUS_CONFIG = {
  not_started: { bg: 'bg-slate-500/15', text: 'text-slate-400' },
  partial: { bg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  implemented: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
  verified: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  deferred: { bg: 'bg-gray-500/15', text: 'text-gray-400' },
};

const Badge = ({ value, tKey, t }) => {
  const cfg = STATUS_CONFIG[value] || { bg: 'bg-gray-500/15', text: 'text-gray-400' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`} data-testid={`badge-${value}`}>
      {tKey ? t(tKey) : value?.replace(/_/g, ' ')}
    </span>
  );
};

// =============== CREATE RECOVERY MODAL ===============

const CreateRecoveryModal = ({ assessmentId, onClose, onCreated, t }) => {
  const [title, setTitle] = useState('');
  const [domain, setDomain] = useState('incident_response');
  const [status, setStatus] = useState('not_started');
  const [ownerName, setOwnerName] = useState('');
  const [notes, setNotes] = useState('');
  const [evidence, setEvidence] = useState(false);
  const [lastTested, setLastTested] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!title.trim()) { setError(t('common:messages.requiredField')); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/governance/assessments/${assessmentId}/recovery`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: title.trim(), domain, status, owner_name: ownerName.trim(),
          notes: notes.trim(), evidence_present: evidence,
          last_tested_at: lastTested ? new Date(lastTested).toISOString() : '',
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || t('common:messages.operationFailed')); }
      onCreated(await res.json());
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-recovery-modal">
      <div className="bg-[#1A1F2B] border border-[rgba(255,255,255,0.08)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[rgba(255,255,255,0.05)]">
          <h3 className="text-white text-lg font-semibold">{t('governance:recoveryCreate.title')}</h3>
          <p className="text-[#8B95A5] text-xs mt-1">{t('governance:recoveryCreate.description')}</p>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded p-2" data-testid="create-error">{error}</div>}
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.titleLabel')} *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder={t('governance:recoveryCreate.titlePlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none"
              data-testid="recovery-title-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.domainLabel')}</label>
              <select value={domain} onChange={e => setDomain(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="recovery-domain-select">
                {Object.keys(DOMAIN_CONFIG).map(d => <option key={d} value={d}>{t(`governance:recoveryDomains.${d}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.statusLabel')}</label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="recovery-status-select">
                {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{t(`governance:recoveryStatuses.${s}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.lastTestedLabel')}</label>
              <input type="date" value={lastTested} onChange={e => setLastTested(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:border-[#36A9E1] focus:outline-none"
                data-testid="recovery-last-tested-input" />
            </div>
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.ownerLabel')}</label>
            <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)}
              placeholder={t('governance:recoveryCreate.ownerPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none"
              data-testid="recovery-owner-input" />
          </div>
          <div>
            <label className="block text-[#8B95A5] text-xs mb-1">{t('governance:recoveryCreate.notesLabel')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder={t('governance:recoveryCreate.notesPlaceholder')}
              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm placeholder-[#555] focus:border-[#36A9E1] focus:outline-none resize-none"
              data-testid="recovery-notes-input" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={evidence} onChange={e => setEvidence(e.target.checked)}
              className="rounded border-[rgba(255,255,255,0.2)]" data-testid="recovery-evidence-checkbox" />
            <label className="text-[#8B95A5] text-xs">{t('governance:recoveryCreate.evidenceLabel')}</label>
          </div>
        </div>
        <div className="p-6 border-t border-[rgba(255,255,255,0.05)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[#8B95A5] text-sm hover:text-white transition-colors" data-testid="cancel-recovery-btn">{t('common:actions.cancel')}</button>
          <button onClick={handleCreate} disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#36A9E1] text-white text-sm font-medium hover:bg-[#2b8bbf] disabled:opacity-50 transition-colors flex items-center gap-2"
            data-testid="confirm-recovery-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {t('governance:recoveryCreate.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============== STAT CARD ===============

const StatCard = ({ value, label, color = 'text-white' }) => (
  <div className="qms-card"><div className="qms-card-body p-3 text-center">
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-[#8B95A5] text-xs mt-0.5">{label}</div>
  </div></div>
);

// =============== MAIN PAGE ===============

const GovernanceReadinessPage = () => {
  const { isAdmin, isAuditor } = useAuth();
  const { t, i18n } = useTranslation(['governance', 'common']);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recoveryItems, setRecoveryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [showCreateRecovery, setShowCreateRecovery] = useState(false);
  const [domainFilter, setDomainFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/governance/assessments`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      setAssessments(await res.json());
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const loadAssessmentData = useCallback(async (assessmentId) => {
    setLoading(true);
    try {
      const [sumRes, recRes] = await Promise.all([
        fetch(`${API}/governance/assessments/${assessmentId}/summary`, { credentials: 'include' }),
        fetch(`${API}/governance/assessments/${assessmentId}/recovery`, { credentials: 'include' }),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      if (recRes.ok) setRecoveryItems(await recRes.json());
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedAssessment) loadAssessmentData(selectedAssessment);
  }, [selectedAssessment, loadAssessmentData]);

  const handleExport = async () => {
    setExporting(true);
    toast.loading(t('governance:pdfExport.exportingPdf'), { id: 'json-export' });
    try {
      const res = await fetch(`${API}/governance/assessments/${selectedAssessment}/export`, { credentials: 'include' });
      if (!res.ok) { const d = await res.json(); toast.error(d.detail || 'Export failed', { id: 'json-export' }); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHARSTAN_Governance_Report_${selectedAssessment}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('governance:pdfExport.downloadStarted'), { id: 'json-export' });
    } catch (err) { console.error(err); toast.error(t('governance:pdfExport.exportFailed'), { id: 'json-export' }); } finally { setExporting(false); }
  };

  const handleExportPdf = async (type) => {
    setExportingPdf(true);
    toast.loading(t('governance:pdfExport.exportingPdf'), { id: 'pdf-export' });
    try {
      const locale = i18n.language || 'en';
      const endpoint = type === 'readiness'
        ? `${API}/governance/readiness/export/pdf?assessment_id=${selectedAssessment}&locale=${locale}`
        : `${API}/governance/posture/export/pdf?assessment_id=${selectedAssessment}&locale=${locale}`;
      const res = await fetch(endpoint, { credentials: 'include' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast.error(d.detail || t('governance:pdfExport.exportFailed'), { id: 'pdf-export' }); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHARSTAN_${type === 'readiness' ? 'Readiness' : 'Posture'}_${selectedAssessment}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('governance:pdfExport.downloadStarted'), { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(t('governance:pdfExport.exportFailed'), { id: 'pdf-export' });
    } finally {
      setExportingPdf(false);
    }
  };

  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:recovery.pageTitle')}>
        <div className="qms-card"><div className="qms-card-body p-8 text-center">
          <Shield className="mx-auto mb-4 text-red-400" size={48} />
          <h3 className="text-white font-semibold mb-2">{t('governance:recovery.accessRestricted')}</h3>
          <p className="text-[#8B95A5] text-sm">{t('governance:recovery.accessRestrictedDesc')}</p>
        </div></div>
      </QMSLayout>
    );
  }

  // Assessment selection view
  if (!selectedAssessment) {
    return (
      <QMSLayout title={t('governance:recovery.pageTitle')}>
        <div className="space-y-6" data-testid="governance-readiness-page">
          <div>
            <h1 className="text-white text-xl font-bold">{t('governance:recovery.pageTitle')}</h1>
            <p className="text-[#8B95A5] text-xs mt-1">{t('governance:recovery.pageDescription')}</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#36A9E1]" size={32} /></div>
          ) : assessments.length === 0 ? (
            <div className="qms-card"><div className="qms-card-body p-8 text-center">
              <Shield className="mx-auto mb-4 text-[#555]" size={48} />
              <p className="text-[#8B95A5] text-sm">No assessments available. Create a gap assessment first.</p>
            </div></div>
          ) : (
            <div className="space-y-3">
              <p className="text-[#8B95A5] text-xs">Select an assessment to view its governance posture, recovery readiness, and notification obligations.</p>
              {assessments.map(a => (
                <div key={a.assessment_id} className="qms-card hover:border-[rgba(54,169,225,0.2)] transition-colors cursor-pointer"
                  onClick={() => setSelectedAssessment(a.assessment_id)}
                  data-testid={`readiness-assessment-${a.assessment_id}`}>
                  <div className="qms-card-body p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{a.assessment_id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${a.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-blue-500/15 text-blue-400'}`}>{a.status}</span>
                      </div>
                      <h3 className="text-white text-sm font-medium">{a.title}</h3>
                      <div className="text-[#555] text-xs mt-1">{t('governance:detail.regions')}: {a.regions?.join(', ') || 'All'} | {t('governance:detail.domains')}: {a.domains?.length || 'All'}</div>
                    </div>
                    <ArrowRight size={16} className="text-[#555]" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </QMSLayout>
    );
  }

  // Filtered recovery items
  const filteredRecovery = domainFilter === 'all' ? recoveryItems : recoveryItems.filter(r => r.domain === domainFilter);

  const TABS = [
    { id: 'summary', label: t('governance:recovery.summaryTab'), icon: TrendingUp },
    { id: 'recovery', label: t('governance:recovery.recoveryTab'), icon: Server },
    { id: 'notification', label: t('governance:recovery.notificationTab'), icon: Bell },
  ];

  return (
    <QMSLayout title={t('governance:recovery.pageTitle')}>
      <div className="space-y-6" data-testid="governance-readiness-detail">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedAssessment(null); setSummary(null); setRecoveryItems([]); }}
            className="text-[#8B95A5] hover:text-white transition-colors" data-testid="back-btn"><ChevronLeft size={20} /></button>
          <div className="flex-1">
            <h2 className="text-white text-lg font-semibold">{summary?.assessment_title || selectedAssessment}</h2>
            <p className="text-[#8B95A5] text-xs">{selectedAssessment} | {t('governance:detail.regions')}: {summary?.posture_score?.regions?.join(', ') || 'All'}</p>
          </div>
          {isAdmin && (
            <button onClick={handleExport} disabled={exporting}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              data-testid="export-btn">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {t('governance:recovery.exportReport')}
            </button>
          )}
          {(isAdmin || isAuditor) && (
            <button onClick={() => handleExportPdf('readiness')} disabled={exportingPdf}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#C9A84C]/20 text-[#C9A84C] text-sm font-medium hover:bg-[#C9A84C]/30 disabled:opacity-50 transition-colors border border-[#C9A84C]/30"
              data-testid="export-readiness-pdf-btn">
              {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              {exportingPdf ? t('governance:pdfExport.exportingPdf') : t('governance:pdfExport.exportPdf')}
            </button>
          )}
          <button onClick={() => loadAssessmentData(selectedAssessment)} className="text-[#8B95A5] hover:text-[#36A9E1] transition-colors p-1" data-testid="refresh-btn"><RefreshCw size={16} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[rgba(255,255,255,0.03)] rounded-lg p-1" data-testid="readiness-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === tab.id ? 'bg-[#36A9E1]/15 text-[#36A9E1]' : 'text-[#8B95A5] hover:text-white'}`}
                data-testid={`tab-${tab.id}`}>
                <Icon size={14} />{tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#36A9E1]" size={32} /></div>
        ) : (
          <>
            {/* SUMMARY TAB */}
            {activeTab === 'summary' && summary && (
              <div className="space-y-6" data-testid="summary-tab">
                {/* Disclaimer */}
                <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)] rounded-lg p-3">
                  <p className="text-[#555] text-xs italic">{t('governance:recovery.disclaimer')}</p>
                </div>

                {/* Posture Score */}
                <div className="qms-card" data-testid="posture-score-section">
                  <div className="qms-card-body p-5">
                    <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-[#36A9E1]" /> {t('governance:postureReport.overallScore')}</h3>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`text-3xl font-bold ${(summary.posture_score?.overall_score || 0) >= 70 ? 'text-emerald-400' : (summary.posture_score?.overall_score || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {summary.posture_score?.overall_score || 0}%
                      </div>
                      <div className="text-[#8B95A5] text-xs">{summary.posture_score?.overall_band || 'N/A'}</div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <StatCard value={summary.posture_score?.total_controls || 0} label={t('governance:detail.totalControls')} />
                      <StatCard value={summary.posture_score?.met_count || 0} label={t('governance:detail.met')} color="text-emerald-400" />
                      <StatCard value={summary.posture_score?.partial_count || 0} label={t('governance:detail.partial')} color="text-yellow-400" />
                      <StatCard value={summary.posture_score?.unmet_count || 0} label={t('governance:detail.unmet')} color="text-red-400" />
                    </div>
                  </div>
                </div>

                {/* Findings Summary */}
                <div className="qms-card" data-testid="findings-summary-section">
                  <div className="qms-card-body p-5">
                    <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3"><Target size={16} className="text-orange-400" /> {t('governance:postureReport.gapFindings')}</h3>
                    <div className="grid grid-cols-5 gap-3">
                      <StatCard value={summary.findings_summary?.total || 0} label="Total" />
                      {['critical', 'high', 'medium', 'low'].map(sev => (
                        <StatCard key={sev} value={summary.findings_summary?.by_severity?.[sev] || 0}
                          label={t(`governance:postureReport.${sev}`)}
                          color={sev === 'critical' ? 'text-red-400' : sev === 'high' ? 'text-orange-400' : sev === 'medium' ? 'text-yellow-400' : 'text-green-400'} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Remediation Summary */}
                <div className="qms-card" data-testid="remediation-summary-section">
                  <div className="qms-card-body p-5">
                    <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3"><Activity size={16} className="text-blue-400" /> {t('governance:postureReport.remediationStatus')}</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                      <StatCard value={summary.remediation_summary?.total || 0} label="Total" />
                      <StatCard value={summary.remediation_summary?.by_status?.planned || 0} label={t('governance:postureReport.planned')} color="text-slate-400" />
                      <StatCard value={summary.remediation_summary?.by_status?.in_progress || 0} label={t('governance:postureReport.inProgress')} color="text-blue-400" />
                      <StatCard value={summary.remediation_summary?.by_status?.verified || 0} label={t('governance:postureReport.verified')} color="text-emerald-400" />
                      <StatCard value={summary.remediation_summary?.escalated_count || 0} label={t('governance:postureReport.escalated')} color="text-orange-400" />
                      <StatCard value={summary.remediation_summary?.overdue_count || 0} label={t('governance:postureReport.overdue')} color="text-red-400" />
                    </div>
                  </div>
                </div>

                {/* Recovery Summary */}
                <div className="qms-card" data-testid="recovery-summary-section">
                  <div className="qms-card-body p-5">
                    <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3"><Server size={16} className="text-cyan-400" /> {t('governance:postureReport.recoveryReadiness')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <StatCard value={summary.recovery_summary?.total || 0} label="Total" />
                      <StatCard value={summary.recovery_summary?.by_status?.verified || 0} label={t('governance:postureReport.verified')} color="text-emerald-400" />
                      <StatCard value={summary.recovery_summary?.evidence_present_count || 0} label={t('governance:postureReport.evidenceDocumented')} color="text-blue-400" />
                      <StatCard value={summary.recovery_summary?.tested_count || 0} label={t('governance:postureReport.tested')} color="text-cyan-400" />
                    </div>
                  </div>
                </div>

                {/* Notification Summary */}
                <div className="qms-card" data-testid="notification-summary-section">
                  <div className="qms-card-body p-5">
                    <h3 className="text-white text-sm font-semibold flex items-center gap-2 mb-3"><Bell size={16} className="text-amber-400" /> {t('governance:postureReport.notificationReadiness')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <StatCard value={summary.notification_readiness?.total_obligations || 0} label={t('governance:notification.totalObligations')} />
                      {Object.entries(summary.notification_readiness?.by_region || {}).slice(0, 3).map(([reg, count]) => (
                        <StatCard key={reg} value={count} label={reg} color="text-amber-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* RECOVERY TAB */}
            {activeTab === 'recovery' && (
              <div className="space-y-4" data-testid="recovery-tab">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)}
                      className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none"
                      data-testid="recovery-domain-filter">
                      <option value="all">{t('governance:recovery.allDomains')}</option>
                      {Object.keys(DOMAIN_CONFIG).map(d => <option key={d} value={d}>{t(`governance:recoveryDomains.${d}`)}</option>)}
                    </select>
                    <span className="text-[#8B95A5] text-xs">{recoveryItems.length} items</span>
                  </div>
                  <button onClick={() => setShowCreateRecovery(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#36A9E1] text-white text-xs font-medium hover:bg-[#2b8bbf] transition-colors"
                    data-testid="add-recovery-btn">
                    <Plus size={14} /> {t('governance:recovery.newRecoveryItem')}
                  </button>
                </div>
                {filteredRecovery.length === 0 ? (
                  <div className="qms-card"><div className="qms-card-body p-8 text-center">
                    <Server className="mx-auto mb-3 text-[#555]" size={32} />
                    <p className="text-[#8B95A5] text-sm">{t('governance:recovery.noRecoveryItems')}</p>
                    <p className="text-[#555] text-xs mt-1">{t('governance:recovery.noRecoveryItemsDesc')}</p>
                  </div></div>
                ) : (
                  <div className="space-y-2">
                    {filteredRecovery.map(r => {
                      const domCfg = DOMAIN_CONFIG[r.domain] || DOMAIN_CONFIG.incident_response;
                      return (
                        <div key={r.recovery_id} className="qms-card" data-testid={`recovery-card-${r.recovery_id}`}>
                          <div className="qms-card-body p-4">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-mono text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{r.recovery_id}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${domCfg.bg} ${domCfg.color}`}>{t(`governance:recoveryDomains.${r.domain}`)}</span>
                                <Badge value={r.status} tKey={`governance:recoveryStatuses.${r.status}`} t={t} />
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {r.evidence_present && <CheckCircle2 size={14} className="text-emerald-400" title={t('governance:recovery.evidencePresent')} />}
                              </div>
                            </div>
                            <h4 className="text-white text-sm font-medium">{r.title}</h4>
                            {r.notes && <p className="text-[#8B95A5] text-xs mt-1">{r.notes}</p>}
                            <div className="flex items-center gap-4 mt-2 text-[#555] text-xs">
                              <span>{t('governance:recovery.owner')}: {r.owner_name}</span>
                              {r.last_tested_at && <span>{t('governance:recovery.lastTested')}: {new Date(r.last_tested_at).toLocaleDateString()}</span>}
                              <span>{t('governance:recovery.created')}: {new Date(r.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {showCreateRecovery && (
                  <CreateRecoveryModal assessmentId={selectedAssessment} t={t}
                    onClose={() => setShowCreateRecovery(false)}
                    onCreated={() => { setShowCreateRecovery(false); loadAssessmentData(selectedAssessment); }} />
                )}
              </div>
            )}

            {/* NOTIFICATION TAB */}
            {activeTab === 'notification' && summary && (
              <div className="space-y-4" data-testid="notification-tab">
                {/* Advisory */}
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-amber-400 text-xs">{t('governance:notification.advisoryNote')}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="qms-card"><div className="qms-card-body p-4 text-center">
                    <Bell className="mx-auto mb-2 text-amber-400" size={20} />
                    <div className="text-amber-400 text-lg font-bold" data-testid="notif-total">{summary.notification_readiness?.total_obligations || 0}</div>
                    <div className="text-[#8B95A5] text-xs">{t('governance:notification.totalObligations')}</div>
                  </div></div>
                  <div className="qms-card"><div className="qms-card-body p-4 text-center">
                    <Eye className="mx-auto mb-2 text-blue-400" size={20} />
                    <div className="text-blue-400 text-lg font-bold" data-testid="notif-regions">{Object.keys(summary.notification_readiness?.by_region || {}).length}</div>
                    <div className="text-[#8B95A5] text-xs">{t('governance:notification.byRegion')}</div>
                  </div></div>
                  <div className="qms-card"><div className="qms-card-body p-4 text-center">
                    <ShieldCheck className="mx-auto mb-2 text-emerald-400" size={20} />
                    <div className="text-emerald-400 text-lg font-bold">3</div>
                    <div className="text-[#8B95A5] text-xs">Readiness Areas</div>
                  </div></div>
                </div>

                {/* Readiness Areas */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { key: 'clientReadiness', icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { key: 'regulatorReadiness', icon: ShieldCheck, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                    { key: 'internalReadiness', icon: Activity, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                  ].map(area => (
                    <div key={area.key} className="qms-card" data-testid={`readiness-area-${area.key}`}>
                      <div className={`qms-card-body p-4 ${area.bg} rounded-lg`}>
                        <area.icon className={`mb-2 ${area.color}`} size={18} />
                        <h4 className="text-white text-xs font-medium">{t(`governance:notification.${area.key}`)}</h4>
                        <p className="text-[#8B95A5] text-xs mt-1">
                          {area.key === 'clientReadiness' && `${summary.notification_readiness?.by_region ? Object.values(summary.notification_readiness.by_region).reduce((a, b) => a + b, 0) : 0} obligations`}
                          {area.key === 'regulatorReadiness' && `${Object.keys(summary.notification_readiness?.by_region || {}).length} regions covered`}
                          {area.key === 'internalReadiness' && 'Escalation & review workflows'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Obligations List */}
                {(summary.notification_readiness?.obligations || []).length === 0 ? (
                  <div className="qms-card"><div className="qms-card-body p-8 text-center">
                    <Bell className="mx-auto mb-3 text-[#555]" size={32} />
                    <p className="text-[#8B95A5] text-sm">{t('governance:notification.noObligations')}</p>
                  </div></div>
                ) : (
                  <div className="space-y-2">
                    {(summary.notification_readiness?.obligations || []).map((ob, idx) => (
                      <div key={ob.obligation_id || idx} className="qms-card" data-testid={`obligation-card-${ob.obligation_id}`}>
                        <div className="qms-card-body p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">{ob.obligation_id}</span>
                            <span className="text-xs text-[#36A9E1] bg-[rgba(54,169,225,0.1)] px-1.5 py-0.5 rounded">{ob.region}</span>
                            <span className="text-xs text-[#8B95A5]">{ob.category?.replace(/_/g, ' ')}</span>
                          </div>
                          <h4 className="text-white text-sm font-medium">{ob.title}</h4>
                          <p className="text-[#8B95A5] text-xs mt-1">{ob.description}</p>
                          {ob.notification_notes && <p className="text-[#555] text-xs mt-1 italic">{ob.notification_notes}</p>}
                          <div className="text-[#555] text-xs mt-1">{t('governance:notification.reference')}: {ob.reference_label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </QMSLayout>
  );
};

export default GovernanceReadinessPage;
